import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Star, Globe, MapPin, Phone, Ban, UserPlus, Bookmark, Trash2, ShieldAlert, Loader2 } from "lucide-react";

const errMsg = (e) => e?.response?.data?.detail?.toString?.() || "Something went wrong";
const STATUSES = ["all", "new", "saved", "converted", "rejected"];

function UsageBanner({ usage }) {
  if (!usage) return null;
  if (!usage.company_enabled) return <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="finder-disabled">Business Finder is disabled for your company. Contact WaggleAI support.</p>;
  return (
    <div className="flex flex-wrap gap-2 text-xs" data-testid="finder-usage">
      <Badge className="bg-slate-100 text-slate-700">Searches this month: {usage.used_this_month}{usage.monthly_limit != null ? ` / ${usage.monthly_limit}` : " · unlimited"}</Badge>
      {!usage.google_ready && <Badge className="bg-amber-100 text-amber-800" data-testid="google-unavailable">Google search not available yet</Badge>}
    </div>
  );
}

function SearchBar({ providers, onResults }) {
  const [f, setF] = useState({ provider: "demo", query: "", location: "", limit: 20 });
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    if (f.query.trim().length < 2) return toast.error("Type what kind of business to find");
    setBusy(true);
    try { const { data } = await api.post("/lead-finder/search", { ...f, location: f.location || null, limit: Number(f.limit) }); toast.success(`${data.results.length} businesses found`); onResults(data.search_id); }
    catch (e2) { toast.error(errMsg(e2)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={run} className="grid grid-cols-1 md:grid-cols-[260px_1fr_1fr_90px_auto] gap-2" data-testid="finder-search-form">
      <Select value={f.provider} onValueChange={(v) => setF({ ...f, provider: v })}>
        <SelectTrigger data-testid="finder-provider-select"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">
          {providers.map((p) => <SelectItem key={p.id} value={p.id} disabled={!p.configured} data-testid={`provider-option-${p.id}`}>{p.label}{!p.configured ? " (unavailable)" : ""}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input placeholder="e.g. real estate agents, clinics, restaurants" value={f.query} onChange={(e) => setF({ ...f, query: e.target.value })} data-testid="finder-query-input" />
      <Input placeholder="City or area (optional)" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="finder-location-input" />
      <Input type="number" min={1} max={60} value={f.limit} onChange={(e) => setF({ ...f, limit: e.target.value })} data-testid="finder-limit-input" />
      <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="finder-search-button">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Find
      </Button>
    </form>
  );
}

function ProspectRow({ p, checked, onCheck, onStatus, onDelete }) {
  return (
    <div className="flex items-start gap-3 p-3 border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`prospect-row-${p.id}`}>
      <Checkbox checked={checked} disabled={p.status === "converted" || p.status === "rejected"} onCheckedChange={onCheck} className="mt-1" data-testid={`prospect-check-${p.id}`} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-slate-900">{p.name}</span>
          <Badge variant="outline" className="text-[10px] uppercase">{p.source}</Badge>
          {p.is_demo && <Badge className="bg-amber-100 text-amber-800 text-[10px]">DEMO DATA</Badge>}
          <Badge className="bg-slate-100 text-slate-700 text-[10px]" data-testid={`prospect-status-${p.id}`}>{p.status}</Badge>
          {p.in_contacts && p.status !== "converted" && <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">Already a contact</Badge>}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          {p.category && <span>{p.category}</span>}
          {p.phone ? <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span> : <span className="text-rose-600">No phone</span>}
          {p.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.address}</span>}
          {p.rating && <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-500" />{p.rating} ({p.reviews || 0})</span>}
          {p.followers ? <span>{p.followers.toLocaleString()} subscribers</span> : null}
          {(p.website || p.url) && <a href={p.website || p.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-emerald-700 hover:underline"><Globe className="w-3 h-3" />link</a>}
        </div>
      </div>
      {p.status !== "converted" && (
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" title="Save" onClick={() => onStatus("saved")} data-testid={`prospect-save-${p.id}`}><Bookmark className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" title="Reject" onClick={() => onStatus("rejected")} data-testid={`prospect-reject-${p.id}`}><Ban className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" title="Delete" onClick={onDelete} data-testid={`prospect-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      )}
    </div>
  );
}

function UnavailableSources({ items }) {
  return (
    <Card className="p-4 border-slate-200" data-testid="unavailable-sources">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2"><ShieldAlert className="w-4 h-4 text-amber-600" /> Not available (no official search API)</div>
      <div className="space-y-1.5">
        {items.map((u) => <div key={u.id} className="text-xs text-slate-600"><b>{u.label}:</b> {u.reason}</div>)}
      </div>
      <a href="/waggleai-feasibility-report.pdf" target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-xs text-emerald-700 hover:underline" data-testid="feasibility-report-link">Read the full source report (PDF)</a>
    </Card>
  );
}

export default function LeadFinder() {
  const nav = useNavigate();
  const [prov, setProv] = useState({ available: [], unavailable: [] });
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [searchId, setSearchId] = useState(null);
  const [sel, setSel] = useState([]);

  const loadProv = () => api.get("/lead-finder/providers").then((r) => setProv(r.data));
  const load = () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (searchId) params.search_id = searchId;
    api.get("/lead-finder/prospects", { params }).then((r) => { setItems(r.data); setSel([]); });
  };
  useEffect(() => { loadProv(); }, []);
  useEffect(load, [status, searchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPStatus = async (id, s) => { try { await api.patch(`/lead-finder/prospects/${id}`, { status: s }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (id) => { try { await api.delete(`/lead-finder/prospects/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const convert = async () => {
    try {
      const { data } = await api.post("/lead-finder/prospects/convert", { ids: sel });
      toast.success(`${data.created} new contacts, ${data.linked} matched existing, ${data.skipped} skipped`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="lead-finder-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Lead Finder</h1>
          <p className="text-sm text-slate-500">Find businesses through official sources, review them, then add the good ones to your CRM.</p>
        </div>
      </div>

      <Card className="p-4 border-slate-200 space-y-3">
        <UsageBanner usage={prov.usage} />
        {prov.usage?.company_enabled !== false && <SearchBar providers={prov.available} onResults={(id) => { setStatus("all"); setSearchId(id); loadProv(); }} />}
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="consent-notice">
          Businesses found here have <b>not</b> agreed to WhatsApp messages. They are added with consent "unknown" and are skipped by campaigns until they opt in.
        </p>
      </Card>

      <div className="grid lg:grid-cols-[1fr_300px] gap-5">
        <Card className="border-slate-200 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b border-slate-100">
            <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => { setStatus(s); setSearchId(null); }} data-testid={`prospect-filter-${s}`}
                  className={`px-3 py-1 text-xs rounded capitalize ${status === s && !searchId ? "bg-white shadow-sm font-medium" : "text-slate-600"}`}>{s}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {searchId && <Badge className="bg-emerald-50 text-emerald-700" data-testid="latest-search-badge">Latest search</Badge>}
              <Button size="sm" disabled={!sel.length} onClick={convert} className="bg-emerald-600 hover:bg-emerald-700" data-testid="convert-selected-button">
                <UserPlus className="w-4 h-4 mr-1" /> Add {sel.length || ""} to CRM
              </Button>
              <Button size="sm" variant="ghost" onClick={() => nav("/leads")} data-testid="go-to-leads">Open Leads</Button>
            </div>
          </div>
          <div data-testid="prospect-list">
            {items.length === 0 && <div className="p-10 text-center text-sm text-slate-500" data-testid="prospects-empty">No businesses yet. Run a search above.</div>}
            {items.map((p) => (
              <ProspectRow key={p.id} p={p} checked={sel.includes(p.id)}
                onCheck={(v) => setSel(v ? [...sel, p.id] : sel.filter((x) => x !== p.id))}
                onStatus={(s) => setPStatus(p.id, s)} onDelete={() => del(p.id)} />
            ))}
          </div>
        </Card>
        <UnavailableSources items={prov.unavailable} />
      </div>
    </div>
  );
}
